// ============================================================================
// engagementTracking — registra logins e tempo de uso por usuário
// ============================================================================
// Storage: localStorage 'fk_engagement_v1' =
//   { [userId]: {
//       userId, role, name,
//       loginCount: number,
//       firstLoginAt: ISO string,
//       lastLoginAt: ISO string,
//       lastActivityAt: ISO string,
//       totalActiveMs: number,                     // tempo agregado de TODAS as sessões
//       currentSessionStartAt: ISO string | null,  // sessão atualmente aberta (null = offline)
//       currentSessionLastBeatAt: ISO string | null,
//       sessions: [                                // histórico
//         { startAt, endAt, durationMs }
//       ]
//   } }
//
// ─── Multi-device sync (importante!) ────────────────────────────────────────
// Cada device só "possui" o registro do usuário atualmente logado nele.
// Se a gente fizesse `cloudSave(KEY, localData)` direto, cada device
// sobrescreveria a cloud com APENAS o registro dele — apagando todos os
// outros usuários. Resultado: admin abria o relatório e via só o próprio
// engajamento, nunca o dos profissionais nos celulares deles.
//
// Solução: antes de subir pra cloud, baixa o estado da cloud, mergeia
// preservando os registros dos OUTROS usuários, e só então sobe.
// `loadEngagementFromCloud()` puxa pra exibir no relatório do admin.
// ============================================================================

import { cloudSave, cloudLoad, confirmCloudMeta } from './supabase.js';
import { SUPABASE_URL, restHeaders } from './secureRest.js';

const KEY = 'fk_engagement_v1';
const HEARTBEAT_INTERVAL_MS = 15_000;       // 15s entre batidas
const STALE_SESSION_THRESHOLD_MS = 90_000;  // 90s sem batida = sessão considerada perdida

function readAll() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); }
  catch { return {}; }
}

function writeLocal(data) {
  try { localStorage.setItem(KEY, JSON.stringify(data)); }
  catch { /* quota? ignora */ }
}

// Mescla `local` em cima de `cloud` por usuário, vencendo o registro de
// `lastActivityAt` mais recente. Garante que ninguém sobrescreve os dados
// de outro usuário ao subir um update local.
function mergeByUserPreferLatest(cloud, local) {
  const merged = { ...(cloud || {}) };
  for (const [uid, localRec] of Object.entries(local || {})) {
    const cloudRec = merged[uid];
    if (!cloudRec) { merged[uid] = localRec; continue; }
    const localTs = localRec?.lastActivityAt ? new Date(localRec.lastActivityAt).getTime() : 0;
    const cloudTs = cloudRec?.lastActivityAt ? new Date(cloudRec.lastActivityAt).getTime() : 0;
    if (localTs >= cloudTs) merged[uid] = localRec;
    // else mantém cloud (mais recente — outro device deve ter atualizado)
  }
  return merged;
}

// Sincroniza local com a cloud preservando os registros de outros users.
// Sequência: cloudLoad → merge (local em cima de cloud) → escreve no
// localStorage o resultado mesclado (assim outros users aparecem aqui tb)
// → cloudSave do mesclado.
let _syncInflight = null;
async function syncWithCloud() {
  // Concorrente? deduplica — todo mundo espera o primeiro terminar.
  if (_syncInflight) return _syncInflight;
  _syncInflight = (async () => {
    try {
      const cloud = (await cloudLoad(KEY)) || {};
      const local = readAll();
      const merged = mergeByUserPreferLatest(cloud, local);
      writeLocal(merged);
      confirmCloudMeta(KEY); // aplicou → pode fixar o ETag
      await cloudSave(KEY, merged);
    } catch {
      // Offline ou cloud com problema: ignora silenciosamente. O próximo
      // heartbeat tenta de novo. localStorage segue como fonte de verdade.
    } finally {
      _syncInflight = null;
    }
  })();
  return _syncInflight;
}

// Heartbeat sobe pra cloud só 1 a cada 4 batidas (~1min) pra economizar.
// Login/logout sobem na hora.
let _heartbeatWriteCount = 0;
function maybeSyncCloud(opts = {}) {
  if (opts.urgent) {
    syncWithCloud();
  } else {
    _heartbeatWriteCount++;
    if (_heartbeatWriteCount >= 4) {
      _heartbeatWriteCount = 0;
      syncWithCloud();
    }
  }
}

function ensureUser(data, userId, role, name) {
  if (!data[userId]) {
    data[userId] = {
      userId,
      role: role || '',
      name: name || '',
      loginCount: 0,
      firstLoginAt: null,
      lastLoginAt: null,
      lastActivityAt: null,
      totalActiveMs: 0,
      currentSessionStartAt: null,
      currentSessionLastBeatAt: null,
      sessions: [],
    };
  } else {
    if (role) data[userId].role = role;
    if (name) data[userId].name = name;
  }
  return data[userId];
}

// Fecha qualquer sessão pendente (estimando endAt pelo último heartbeat).
function closeStaleSession(record) {
  if (!record.currentSessionStartAt) return;
  const startAt = record.currentSessionStartAt;
  const lastBeat = record.currentSessionLastBeatAt || startAt;
  const durationMs = Math.max(0, new Date(lastBeat).getTime() - new Date(startAt).getTime());
  record.sessions.push({ startAt, endAt: lastBeat, durationMs });
  // Mantém apenas últimas 200 sessões para não estourar storage
  if (record.sessions.length > 200) record.sessions = record.sessions.slice(-200);
  record.currentSessionStartAt = null;
  record.currentSessionLastBeatAt = null;
}

export function recordLogin({ userId, role, name }) {
  if (!userId) return;
  const data = readAll();
  const rec = ensureUser(data, userId, role, name);
  // Se há sessão aberta (não foi fechada por algum motivo), feche antes.
  closeStaleSession(rec);
  const now = new Date().toISOString();
  rec.loginCount = (rec.loginCount || 0) + 1;
  rec.firstLoginAt = rec.firstLoginAt || now;
  rec.lastLoginAt = now;
  rec.lastActivityAt = now;
  rec.currentSessionStartAt = now;
  rec.currentSessionLastBeatAt = now;
  writeLocal(data);
  maybeSyncCloud({ urgent: true }); // login → sobe pra cloud na hora
}

export function recordHeartbeat(userId) {
  if (!userId) return;
  const data = readAll();
  const rec = data[userId];
  if (!rec || !rec.currentSessionStartAt) return;
  const now = new Date();
  const lastBeat = new Date(rec.currentSessionLastBeatAt || rec.currentSessionStartAt);
  const elapsedMs = Math.max(0, now.getTime() - lastBeat.getTime());
  // Se passou muito tempo sem batida, presume que ficou inativo: descarta o gap.
  // Senão, soma o gap ao tempo ativo.
  if (elapsedMs <= STALE_SESSION_THRESHOLD_MS) {
    rec.totalActiveMs = (rec.totalActiveMs || 0) + elapsedMs;
  }
  rec.currentSessionLastBeatAt = now.toISOString();
  rec.lastActivityAt = rec.currentSessionLastBeatAt;
  writeLocal(data);
  maybeSyncCloud();
}

export function recordLogout(userId) {
  if (!userId) return;
  const data = readAll();
  const rec = data[userId];
  if (!rec) return;
  closeStaleSession(rec);
  writeLocal(data);
  maybeSyncCloud({ urgent: true }); // logout → sobe pra cloud na hora
}

export function getEngagementData() {
  return readAll();
}

export function getEngagementForUser(userId) {
  return readAll()[userId] || null;
}

// ─── auth_log → estatísticas autoritativas de login ─────────────────────────
// O loginCount/firstLoginAt/lastLoginAt do engagement local pode dessincronizar
// (sessionStorage por aba, race conditions de cloudSave entre devices, etc).
// O auth_log é registrado SERVER-SIDE em cada chamada /api/auth-login com
// success=true — fonte autoritativa.
//
// Esta função paginа o auth_log e agrega por user_id.
// Retorna: { [userId]: { loginCount, firstLoginAt, lastLoginAt } }
// 06/10/2026 (Financeiro Fase 0): o auth_log ficou SÓ do master no /api/db.
// REGRA LINEAR: falha (403 inclusive) LANÇA — antes dava 'break' e devolvia
// {} (ou meia contagem), e a tela mostrava os números do registro local como
// se fossem os oficiais. err.status = HTTP (403 = sem acesso).
export async function fetchAuthLogLoginStats() {
  const PAGE_SIZE = 1000;
  const MAX_PAGES = 20; // 20k entries cap — bem mais que necessário
  const stats = {};
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `${SUPABASE_URL}/rest/v1/auth_log?action=eq.login&success=eq.true`
              + `&select=user_id,created_at&order=created_at.asc`
              + `&limit=${PAGE_SIZE}&offset=${offset}`;
    const r = await fetch(url, {
      headers: restHeaders(),
    });
    if (!r.ok) {
      const err = new Error(`HTTP ${r.status} em /api/db/rest/v1/auth_log`);
      err.status = r.status;
      throw err;
    }
    const rows = await r.json();
    if (!rows || rows.length === 0) break;
    for (const row of rows) {
      const uid = row.user_id;
      if (!uid) continue;
      if (!stats[uid]) {
        stats[uid] = { loginCount: 0, firstLoginAt: row.created_at, lastLoginAt: row.created_at };
      }
      stats[uid].loginCount++;
      // rows vêm ordenadas ASC, então last sempre é o atual; first é o primeiro encontrado
      stats[uid].lastLoginAt = row.created_at;
    }
    if (rows.length < PAGE_SIZE) break;
    offset += PAGE_SIZE;
  }
  return stats;
}

// Mescla engagement (sessions, totalActiveMs) com stats do auth_log (loginCount,
// firstLoginAt, lastLoginAt). auth_log é fonte autoritativa pra esses 3 campos.
function mergeWithAuthLogStats(engagement, authStats) {
  const merged = { ...(engagement || {}) };
  for (const [uid, stats] of Object.entries(authStats || {})) {
    const existing = merged[uid] || {
      userId: uid, role: '', name: '',
      totalActiveMs: 0, currentSessionStartAt: null,
      currentSessionLastBeatAt: null, sessions: [],
    };
    merged[uid] = {
      ...existing,
      loginCount: stats.loginCount,
      firstLoginAt: stats.firstLoginAt,
      lastLoginAt: stats.lastLoginAt,
      // lastActivityAt = o mais recente entre lastLogin e o que tinha
      lastActivityAt: maxIso(stats.lastLoginAt, existing.lastActivityAt),
    };
  }
  return merged;
}

function maxIso(a, b) {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

// ─── Pra uso do relatório do admin: força reload da cloud ───────────────────
// Baixa o estado da cloud, mescla com o local, salva tudo no localStorage.
// Após isso, getEngagementData() retorna a visão completa de TODOS os users.
//
// IMPORTANTE: também enriquece com stats do auth_log (server-side autoritativo)
// pra que loginCount/firstLoginAt/lastLoginAt reflitam TODOS os logins reais —
// não só os que o JS conseguiu sincronizar com sucesso.
// 06/10/2026: { incluirAuthLog } — o auth_log é só do master (quem não é nem
// pede: nada de 403 a cada 60s). Devolve { data, authLog } com authLog =
// 'ok' | 'sem_acesso' | 'falhou' | 'nao_pedido', pra tela dizer de onde vêm
// os números de login (nunca apresentar o registro local como oficial calado).
export async function loadEngagementFromCloud({ incluirAuthLog = true } = {}) {
  let authLog = incluirAuthLog ? 'ok' : 'nao_pedido';
  try {
    const [cloud, authStats] = await Promise.all([
      cloudLoad(KEY).catch(() => ({})),
      incluirAuthLog
        ? fetchAuthLogLoginStats().catch((err) => { authLog = err?.status === 403 ? 'sem_acesso' : 'falhou'; return {}; })
        : Promise.resolve({}),
    ]);
    const local = readAll();
    // Primeiro mescla cloud + local (preserva sessions/totalActiveMs)
    let merged = mergeByUserPreferLatest(cloud || {}, local);
    // Depois sobrescreve loginCount/first/lastLoginAt com auth_log (autoritativo)
    merged = mergeWithAuthLogStats(merged, authStats);
    writeLocal(merged);
    return { data: merged, authLog };
  } catch {
    return { data: readAll(), authLog: authLog === 'ok' ? 'falhou' : authLog };
  }
}

export function clearEngagement() {
  localStorage.removeItem(KEY);
  // Limpa cloud também — admin clicou no reset, espera reset global.
  cloudSave(KEY, {}).catch(() => {});
}

export const ENGAGEMENT_HEARTBEAT_INTERVAL_MS = HEARTBEAT_INTERVAL_MS;
